// Сборка PDF-отчёта на клиенте (Вариант A) из уже захваченных изображений и
// данных stores. Без обращений к бэкенду. Изображения карты/графиков
// приходят готовыми dataURL (PNG) из captureRegistry, таблицы рисуются текстом.
//
// Раскладка: A4 portrait, поля 15 мм, автоматический перенос на новую
// страницу. Карта вписывается в страницу целиком (по ширине и высоте) с
// сохранением пропорций; легенда-палитра уже отрисована на самом снимке
// (см. captureMapImage в MapContainer). Statistics — часть секции Map.

import { jsPDF } from 'jspdf';

const PAGE = { w: 210, h: 297 };
const MARGIN = 15;
const CONTENT_W = PAGE.w - MARGIN * 2;
const CONTENT_H = PAGE.h - MARGIN * 2;

// Натуральные размеры PNG из dataURL — нужны для сохранения пропорций.
function loadImageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

class Cursor {
  constructor(doc) {
    this.doc = doc;
    this.y = MARGIN;
  }

  // Гарантировать место высотой h, иначе новая страница.
  ensure(h) {
    if (this.y + h > PAGE.h - MARGIN) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }

  gap(h) {
    this.y += h;
  }
}

// Рисует заголовок без проверки переноса (перенос уже решён вызывающим).
// Суммарно опускает курсор примерно на 8 мм.
function drawHeading(doc, cur, text, size = 13) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(size);
  doc.setTextColor(33, 37, 41);
  doc.text(text, MARGIN, cur.y);
  cur.gap(2);
  doc.setDrawColor(220);
  doc.line(MARGIN, cur.y, PAGE.w - MARGIN, cur.y);
  cur.gap(6);
  doc.setFont('helvetica', 'normal');
}

// minFollow — сколько места (мм) должно остаться под заголовком, иначе он
// переносится на следующую страницу (чтобы заголовок не висел внизу один).
function addHeading(doc, cur, text, size = 13, minFollow = 22) {
  cur.ensure(size * 0.5 + 4 + minFollow);
  drawHeading(doc, cur, text, size);
}

// Высота, на которую drawHeading опускает курсор (для совместного ensure).
const HEADING_BLOCK_H = 8;

function addKeyValueLines(doc, cur, lines, size = 10) {
  doc.setFontSize(size);
  doc.setTextColor(60);
  lines.forEach(({ label, value }) => {
    cur.ensure(size * 0.5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}: `, MARGIN, cur.y);
    const labelW = doc.getTextWidth(`${label}: `);
    doc.setFont('helvetica', 'normal');
    const valStr = value == null || value === '' ? '—' : String(value);
    const wrapped = doc.splitTextToSize(valStr, CONTENT_W - labelW);
    doc.text(wrapped, MARGIN + labelW, cur.y);
    cur.gap(size * 0.5 * Math.max(1, wrapped.length) + 1.5);
  });
}

// Вставка изображения: по ширине контента, но не выше maxH (по умолчанию —
// высота контента страницы); при превышении масштабируется вниз и
// центрируется. Если передан headingText, заголовок раздела рисуется ВМЕСТЕ с
// картинкой под единым решением о переносе — так заголовок не остаётся внизу
// страницы, когда картинка уезжает на следующую.
async function addImage(doc, cur, dataUrl, caption, maxH = CONTENT_H - 12, headingText = null, headingSize = 13) {
  if (!dataUrl) return;
  const size = await loadImageSize(dataUrl);
  if (!size || !size.w || !size.h) return;
  let drawW = CONTENT_W;
  let drawH = (size.h / size.w) * drawW;
  if (drawH > maxH) {
    drawH = maxH;
    drawW = (size.w / size.h) * drawH;
  }
  const x = MARGIN + (CONTENT_W - drawW) / 2;
  const headingH = headingText ? HEADING_BLOCK_H : 0;
  const captionH = caption ? 6 : 0;
  // Единый перенос: заголовок + подпись + картинка не разрываются.
  cur.ensure(headingH + captionH + drawH);

  if (headingText) drawHeading(doc, cur, headingText, headingSize);
  if (caption) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(caption, MARGIN, cur.y);
    cur.gap(3);
  }
  doc.addImage(dataUrl, 'PNG', x, cur.y, drawW, drawH, undefined, 'FAST');
  cur.gap(drawH + 6);
}

// Простая таблица: header — массив строк, rows — массив массивов строк.
// weights — относительные ширины колонок (по умолчанию равные).
function addTable(doc, cur, header, rows, weights) {
  const cols = header.length;
  const w = (weights && weights.length === cols) ? weights : header.map(() => 1);
  const total = w.reduce((a, b) => a + b, 0);
  // x-смещение левого края каждой колонки.
  const offsets = [];
  let acc = 0;
  w.forEach((ww) => { offsets.push((acc / total) * CONTENT_W); acc += ww; });
  const colW = (i) => (w[i] / total) * CONTENT_W;
  const rowH = 7;
  const drawRow = (cells, isHeader) => {
    cur.ensure(rowH);
    doc.setFontSize(9);
    doc.setFont('helvetica', isHeader ? 'bold' : 'normal');
    if (isHeader) {
      doc.setFillColor(238, 240, 242);
      doc.rect(MARGIN, cur.y - 4.5, CONTENT_W, rowH, 'F');
    }
    doc.setDrawColor(225);
    doc.line(MARGIN, cur.y + rowH - 4.5, PAGE.w - MARGIN, cur.y + rowH - 4.5);
    doc.setTextColor(40);
    cells.forEach((c, i) => {
      const txt = doc.splitTextToSize(String(c == null ? '' : c), colW(i) - 3)[0] || '';
      doc.text(txt, MARGIN + offsets[i] + 1.5, cur.y);
    });
    cur.gap(rowH);
  };
  drawRow(header, true);
  rows.forEach((r) => drawRow(r, false));
  cur.gap(4);
}

// Имя файла LiDAR_Report_{SiteName}_{YYYYMMDD_HHmm}.pdf; сегмент Site
// опускается, если сайт не выбран.
function reportFileName(site, now) {
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const siteSegment = site
    ? `_${String(site).trim().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`
    : '';
  return `LiDAR_Report${siteSegment}_${stamp}.pdf`;
}

export async function buildReportPdf(report) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const cur = new Cursor(doc);
  const now = new Date();

  // --- Шапка ---
  const title = report.site ? `LiDAR Report — ${report.site}` : 'LiDAR Report';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(20, 30, 45);
  doc.text(title, MARGIN, cur.y + 4);
  cur.gap(10);

  const meta = report.meta || {};
  addKeyValueLines(doc, cur, [
    { label: 'Generated', value: meta.generatedAt },
    { label: 'Time range', value: meta.timeRange },
  ]);
  cur.gap(2);

  // --- Карта (+ Statistics под ней) ---
  if (report.map) {
    // Заголовок 'Map' рисуется вместе со снимком карты (единый перенос).
    await addImage(doc, cur, report.map, null, CONTENT_H - 12, 'Map');
    if (report.stats && report.stats.rows && report.stats.rows.length) {
      addHeading(doc, cur, 'Statistics', 11);
      if (report.stats.startDate || report.stats.endDate) {
        addKeyValueLines(doc, cur, [
          { label: 'Start date', value: report.stats.startDate },
          { label: 'End date', value: report.stats.endDate },
        ]);
        cur.gap(1);
      }
      addTable(
        doc,
        cur,
        ['Value', 'Min', 'Max', 'AVG', 'RMS', 'Count'],
        report.stats.rows.map((r) => [r.label, r.min, r.max, r.avg, r.rms, r.count]),
        [3, 1, 1, 1, 1, 1]
      );
    }
  }

  // --- Графики Charts ---
  if (report.charts && report.charts.length) {
    // Заголовок 'Charts' привязан к первому графику (единый перенос), чтобы
    // не остаться внизу страницы. Заголовок самого графика — внутри картинки.
    for (let i = 0; i < report.charts.length; i += 1) {
      await addImage(doc, cur, report.charts[i].dataUrl, null, 90, i === 0 ? 'Charts' : null);
    }
  }

  // --- Профайлинг ---
  if (report.profiling) {
    addHeading(doc, cur, 'Profiling');
    if (report.profiling.meta && report.profiling.meta.length) {
      addKeyValueLines(doc, cur, report.profiling.meta);
      cur.gap(2);
    }
    if (report.profiling.images && report.profiling.images.length) {
      for (const img of report.profiling.images) {
        await addImage(doc, cur, img.dataUrl, img.title, 90);
      }
    }
  }

  // --- Подпись на каждой странице ---
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `© LIFeLiDAR — ${meta.generatedAt || ''}`,
      MARGIN,
      PAGE.h - 8
    );
    doc.text(`${i} / ${pageCount}`, PAGE.w - MARGIN, PAGE.h - 8, { align: 'right' });
  }

  const fname = reportFileName(report.site, now);
  doc.save(fname);
  return fname;
}
